import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Suspense } from 'react';
import { Box, Typography, Button } from '@mui/material';
import logger from '@/core/services';
import { appProviderStyles } from './styles/AppProvider.styles';

import { useAppAuthProvider } from '@/core/auth';
import { RefineProvider } from './RefineProvider';
import { Routes } from '@/core/routing';
import { ErrorBoundary } from '@/shared/components/ui';

// Create QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    },
  },
});

export const AppProvider = () => {
  const { t } = useTranslation();

  return (
    <Suspense fallback={<div>{t('loading.authentication')}</div>}>
      <AuthenticatedApp />
    </Suspense>
  );
};

const AuthenticatedApp = () => {
  const { t } = useTranslation();
  const { authProvider, isAuthenticated, user, auth0Error, token } =
    useAppAuthProvider();

  if (!isAuthenticated || !token) {
    return (
      <Box sx={appProviderStyles.redirectingContainer}>
        <Typography>{t('loading.redirectingToLogin')}</Typography>
        {auth0Error && (
          <Box sx={appProviderStyles.authErrorContainer}>
            <Typography variant="h6">
              {t('errors.authenticationError')}
            </Typography>
            <Typography sx={{ color: 'red' }}>{auth0Error.message}</Typography>
          </Box>
        )}
      </Box>
    );
  }

  try {
    logger.info('Rendering authenticated view for user:', user?.name);

    return (
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ErrorBoundary authProvider={authProvider}>
            <RefineProvider authProvider={authProvider}>
              <Routes />
            </RefineProvider>
          </ErrorBoundary>
        </BrowserRouter>
      </QueryClientProvider>
    );
  } catch (error) {
    logger.error('Error rendering authenticated view:', error);
    return (
      <Box sx={appProviderStyles.renderingErrorContainer}>
        <Typography variant="h4">{t('errors.renderingError')}</Typography>
        <Typography>{t('errors.renderingErrorDescription')}</Typography>
        <Box component="pre" sx={appProviderStyles.renderingErrorPre}>
          {error instanceof Error ? error.message : String(error)}
        </Box>
        <Button
          onClick={() => authProvider.logout && authProvider.logout({})}
          sx={appProviderStyles.renderingErrorButton}
        >
          {t('buttons.logout')}
        </Button>
      </Box>
    );
  }
};
